import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FiInfo } from 'react-icons/fi';
import TimeDropdownInput from './TimeDropdownInput';
import { formatPhoneNumber } from '../../utils/phone';
import { generateTimeOptions, composeDockHoursRange, normalizeTime } from '../../utils/time';
import styles from './LocationPicker.module.css';

const contactTemplate = { name: '', role: '', phone: '', ext: '', email: '', notes: '' };

const buildInitialDraft = () => ({
  name: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  dockHoursFrom: '',
  dockHoursTo: '',
  dockType: '',
  notes: '',
  contacts: [],
});

const AddLocationModal = ({ isOpen, onSave, onCancel }) => {
  const [draftLocation, setDraftLocation] = useState(buildInitialDraft);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [contactErrors, setContactErrors] = useState({});
  const [timeOptions] = useState(generateTimeOptions());

  const canSave = useMemo(
    () =>
      Boolean(
        draftLocation.name &&
          draftLocation.street &&
          draftLocation.city &&
          draftLocation.state &&
          draftLocation.zip
      ),
    [draftLocation]
  );

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);
    setError('');
    setContactErrors({});
    const validatedContacts = [];
    const newContactErrors = {};
    draftLocation.contacts.forEach((contact, index) => {
      const trimmed = {
        name: contact.name?.trim() || '',
        phone: contact.phone?.trim() || '',
        ext: contact.ext?.trim() || '',
        email: contact.email?.trim() || '',
        role: contact.role?.trim() || '',
        notes: contact.notes?.trim() || '',
      };
      const isEmpty = Object.values(trimmed).every((value) => value === '');
      if (isEmpty) return;

      const contactError = {};
      if (!trimmed.name) {
        contactError.name = 'Name is required';
      }
      if (!trimmed.phone) {
        contactError.phone = 'Phone is required';
      }

      if (Object.keys(contactError).length > 0) {
        newContactErrors[index] = contactError;
        return;
      }

      validatedContacts.push({ ...trimmed });
    });

    if (Object.keys(newContactErrors).length > 0) {
      setContactErrors(newContactErrors);
      setIsSaving(false);
      setError('Please complete required contact fields.');
      return;
    }

    try {
      const dockHours = composeDockHoursRange(draftLocation.dockHoursFrom, draftLocation.dockHoursTo);
      const payload = { ...draftLocation, dockHours, contacts: validatedContacts };
      const created = await onSave(payload);
      if (created) {
        setDraftLocation(buildInitialDraft());
      }
    } catch (saveError) {
      setError('We could not save this location. Please check the details and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDraftLocation(buildInitialDraft());
    setError('');
    setContactErrors({});
    onCancel();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.dialogHeader}>
          <div>
            <h4>Add new location</h4>
            <p>New locations are saved to your account and shared with your workspace.</p>
          </div>
          <button type="button" className={styles.closeButton} onClick={handleCancel} aria-label="Close add location">
            ×
          </button>
        </div>
        <div className={styles.addForm}>
          <div className={styles.formRow}>
            <label>
              Company name
              <input
                required
                value={draftLocation.name}
                onChange={(event) => setDraftLocation((prev) => ({ ...prev, name: event.target.value }))}
              />
            </label>
            <label>
              Street address
              <input
                required
                value={draftLocation.street}
                onChange={(event) => setDraftLocation((prev) => ({ ...prev, street: event.target.value }))}
              />
            </label>
            <label>
              City
              <input
                required
                value={draftLocation.city}
                onChange={(event) => setDraftLocation((prev) => ({ ...prev, city: event.target.value }))}
              />
            </label>
          </div>

          <div className={styles.formRow}>
            <label>
              State
              <input
                required
                value={draftLocation.state}
                onChange={(event) => setDraftLocation((prev) => ({ ...prev, state: event.target.value }))}
              />
            </label>
            <label>
              ZIP
              <input
                required
                value={draftLocation.zip}
                onChange={(event) => setDraftLocation((prev) => ({ ...prev, zip: event.target.value }))}
              />
            </label>
            <div />
          </div>

          <div className={styles.formRowAligned}>
            <div className={styles.dockHoursGroup}>
              <span className={styles.fieldLabel}>Dock hours</span>
              <div className={styles.dockHoursInputs}>
                <TimeDropdownInput
                  label="From"
                  value={draftLocation.dockHoursFrom}
                  onChange={(value) =>
                    setDraftLocation((prev) => ({ ...prev, dockHoursFrom: normalizeTime(value) }))
                  }
                  onSelect={(value) => setDraftLocation((prev) => ({ ...prev, dockHoursFrom: value }))}
                  options={timeOptions}
                  placeholder="08:00"
                />
                <span className={styles.dockHoursSeparator}>–</span>
                <TimeDropdownInput
                  label="To"
                  value={draftLocation.dockHoursTo}
                  onChange={(value) => setDraftLocation((prev) => ({ ...prev, dockHoursTo: normalizeTime(value) }))}
                  onSelect={(value) => setDraftLocation((prev) => ({ ...prev, dockHoursTo: value }))}
                  options={timeOptions}
                  placeholder="17:00"
                />
              </div>
            </div>
            <label className={styles.fieldLabel}>
              Dock type
              <select
                value={draftLocation.dockType}
                onChange={(event) => setDraftLocation((prev) => ({ ...prev, dockType: event.target.value }))}
              >
                <option value="">Unknown</option>
                <option value="FCFS">FCFS</option>
                <option value="APPOINTMENT">Appointment</option>
              </select>
            </label>
            <div />
          </div>

          <label className={styles.notesField}>
            Notes (optional)
            <textarea
              rows={3}
              value={draftLocation.notes}
              onChange={(event) => setDraftLocation((prev) => ({ ...prev, notes: event.target.value }))}
              placeholder="Security instructions, key contacts, or facility specifics"
            />
          </label>

          <div className={styles.contactsSection}>
            <div className={styles.contactsHeader}>
              <span>Contacts</span>
              <button
                type="button"
                className={styles.addContact}
                onClick={() =>
                  setDraftLocation((prev) => ({
                    ...prev,
                    contacts: [...prev.contacts, { ...contactTemplate }],
                  }))
                }
              >
                + Add contact
              </button>
            </div>
            {draftLocation.contacts.length > 0 && (
              <div className={styles.contactList}>
                {draftLocation.contacts.map((contact, contactIndex) => {
                  const errors = contactErrors[contactIndex] || {};
                  return (
                    <div key={`contact-${contactIndex}`} className={styles.contactCard}>
                      <div className={styles.contactRowGrid}>
                        <label>
                          Name <span className={styles.required}>*</span>
                          <input
                            className={errors.name ? styles.inputError : ''}
                            value={contact.name}
                            onChange={(event) =>
                              setDraftLocation((prev) => {
                                const next = [...prev.contacts];
                                next[contactIndex] = { ...next[contactIndex], name: event.target.value };
                                return { ...prev, contacts: next };
                              })
                            }
                          />
                          {errors.name && <small className={styles.errorText}>{errors.name}</small>}
                        </label>
                        <label>
                          Phone <span className={styles.required}>*</span>
                          <input
                            className={errors.phone ? styles.inputError : ''}
                            value={contact.phone}
                            onChange={(event) =>
                              setDraftLocation((prev) => {
                                const next = [...prev.contacts];
                                next[contactIndex] = {
                                  ...next[contactIndex],
                                  phone: formatPhoneNumber(event.target.value),
                                };
                                return { ...prev, contacts: next };
                              })
                            }
                            placeholder="(555)- 123-4567"
                            inputMode="tel"
                          />
                          {errors.phone && <small className={styles.errorText}>{errors.phone}</small>}
                        </label>
                        <label>
                          Ext
                          <input
                            value={contact.ext}
                            onChange={(event) =>
                              setDraftLocation((prev) => {
                                const next = [...prev.contacts];
                                next[contactIndex] = { ...next[contactIndex], ext: event.target.value };
                                return { ...prev, contacts: next };
                              })
                            }
                          />
                        </label>
                      </div>
                      <div className={styles.contactRowGrid}>
                        <label>
                          Email
                          <input
                            type="email"
                            value={contact.email}
                            onChange={(event) =>
                              setDraftLocation((prev) => {
                                const next = [...prev.contacts];
                                next[contactIndex] = { ...next[contactIndex], email: event.target.value };
                                return { ...prev, contacts: next };
                              })
                            }
                            placeholder="dispatch@example.com"
                          />
                        </label>
                        <label>
                          Role
                          <select
                            value={contact.role}
                            onChange={(event) =>
                              setDraftLocation((prev) => {
                                const next = [...prev.contacts];
                                next[contactIndex] = { ...next[contactIndex], role: event.target.value };
                                return { ...prev, contacts: next };
                              })
                            }
                          >
                            <option value="">Select role</option>
                            <option value="Shipping Supervisor">Shipping Supervisor</option>
                            <option value="Receiving">Receiving</option>
                            <option value="Warehouse">Warehouse</option>
                            <option value="Appointments">Appointments</option>
                            <option value="Clerk">Clerk</option>
                            <option value="AP">AP</option>
                          </select>
                        </label>
                        <label>
                          Notes
                          <input
                            value={contact.notes}
                            onChange={(event) =>
                              setDraftLocation((prev) => {
                                const next = [...prev.contacts];
                                next[contactIndex] = { ...next[contactIndex], notes: event.target.value };
                                return { ...prev, contacts: next };
                              })
                            }
                          />
                        </label>
                      </div>
                      {draftLocation.contacts.length > 0 && (
                        <button
                          type="button"
                          className={styles.removeContact}
                          onClick={() =>
                            setDraftLocation((prev) => ({
                              ...prev,
                              contacts: prev.contacts.filter((_, idx) => idx !== contactIndex),
                            }))
                          }
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.formFooter}>
            <div className={styles.formHint}>
              <FiInfo />
              <p>We automatically add your location to My Locations and All Locations.</p>
            </div>
            <div className={styles.formActions}>
              <button type="button" onClick={handleCancel}>Cancel</button>
              <button type="button" className={styles.primary} disabled={!canSave || isSaving} onClick={handleSave}>
                {isSaving ? 'Saving…' : 'Save location'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AddLocationModal.propTypes = {
  isOpen: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

AddLocationModal.defaultProps = {
  isOpen: false,
};

export default AddLocationModal;
